/**
 *
 * FBForms
 *
 */

import React, { memo, useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// styles
import './style.scss';

// antd component
import { Form, Input, Dropdown, Button } from 'antd';
const { TextArea } = Input;

// icons
import CustomIcons from 'components/CustomIcons';

// components
import ErrorBoundary from 'engine/ErrorBoundary';
import HiddenField from 'components/Forms/Fields/HiddenField';

// Communication components
import {
  FBSectionHeader,
  FBSectionErroMessage,
  FBSectionFieldActions,
} from 'components/Communications';
// inner components
import SectionFields from './components/SectionFields';

// hooks
import { useRefState } from 'utils/hooks';

function validateForm(formData, name = 'forms') {
  let isValid = true;
  if (Array.isArray(formData?.[`${name}-fields`]) && formData?.[`${name}-fields`]?.length > 0) {
    for (let field of formData?.[`${name}-fields`]) {
      if (!field?.['field-title']) {
        isValid = false;
        break;
      }
  
      if (field?.['field-type'] === 'radio' || field?.['field-type'] === 'checkbox') {
        if (!field?.['field-input'] || field?.['field-input']?.some(input => !input?.[`${field?.['field-type']}-input`])) {
          isValid = false;
          break;
        }
      }
    }
  } else {
    isValid = false;
  }
  return isValid;
}

function FBForms({
  name,
  mode,
  method,
  initialValues,
  isValidSection,
  // actions
  setValidSection,
  onDelete,
  onChangeSection,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames(
    'tcf-fb-forms-group',
    'tcf-fb-group',
    className,
  );

  let formName = name;

  const [dynamicForm] = Form.useForm();

  const [validFields, setValidFields] = useState(isValidSection || false);
  const [currentValues, currentValuesRef, setCurrentValues] = useRefState(
    initialValues || {},
  );

  useEffect(() => {
    if (!!initialValues) {
      const isValid = validateForm(initialValues);
      setValidFields(isValid);
      !!setValidSection && setValidSection(name, isValid);
      if (JSON.stringify(initialValues) != JSON.stringify(currentValues)) {
        setCurrentValues(initialValues);
      }
    } else if (!!!initialValues) {
      resetAllStates();
    }

    return () => {
      resetAllStates();
    };
  }, []);

  const resetAllStates = useCallback(() => {
    setCurrentValues({});
    setValidFields(false);
    dynamicForm.resetFields();
  }, [initialValues, currentValues]);

  // Delete Section
  const deleteSection = useCallback(name => {
    resetAllStates();
    onChangeSectionHandler(null);
    !!onDelete && onDelete(name);
  }, []);

  // update Section
  const onChangeSectionHandler = useCallback(
    sectionData => {
      !!onChangeSection && onChangeSection(name, sectionData);
    },
    [currentValues, onChangeSection],
  );

  // custom Validation for Section fileds
  const checkValidCallback = useCallback(
    async (rule, values, callback) => {
      const isValid = validateForm(currentValuesRef?.curent);
      setValidFields(isValid);
      !!setValidSection && setValidSection(name, isValid);
      if (isValid) {
        return Promise.resolve(isValid);
      } else {
        return Promise.reject(
          new Error("L'entrée n'est pas valide."),
          values,
        );
      }
    },
    [setValidSection, currentValuesRef],
  );

  const erroMessage = useMemo(
    () => (
      <FBSectionErroMessage
        showMessage={!validFields}
        message="Cette section nécessite plusieurs champs à remplir."
      />
    ),
    [validFields, isValidSection],
  );

  // Update parent states based on changes inside Form
  const handleValuesChange = useCallback(
    async (changedValues, allValues) => {
      let values = allValues[name] || {};
      const isValid = validateForm(values);
      setValidFields(isValid);
      !!setValidSection && setValidSection(name, isValid);
      !!isValid && setCurrentValues(values);
     // dynamicForm.validateFields();
    },
    [name, currentValues, setValidSection],
  );

  /* Generate Filed based on chozen type */
  const generateRadioOrCheckboxFiled = useCallback((element, fieldType) => {
    const { key, name, fieldKey, ...restField } = element;
    return (
      <>
        <Form.List
          {...restField}
          name={[name, 'field-input']}
          fieldKey={[fieldKey, 'field-input']}
          className={`forms-field ${fieldType}`}
          rules={[
            {
              required: true,
              validator: async (_, vals) => {
                if (!!!vals || vals?.length < 2) {
                  return Promise.reject(new Error('At least 2 option'));
                }
              },
              validateTrigger: 'onChange',
            },
          ]}
        >
          {(fields, { add, remove, move }) => {
            return (
              <>
                {fields.map((element, index) => {
                  const { key, name, fieldKey, ...restField } = element;
                  return (
                    <div
                      key={key + index}
                      className={`forms-row forms-row-${index} ${fieldType}`}
                      align="baseline"
                    >
                      <Form.Item
                        {...restField}
                        name={[name, [`${fieldType}-input`]]}
                        fieldKey={[fieldKey, [`${fieldType}-input`]]}
                        className="forms-field forms-field-title"
                        rules={[
                          {
                            required: true,
                            message: 'Champ obligatoire',
                            validateTrigger: 'onChange',
                          },
                        ]}
                      >
                        <Input placeholder={`Option`} />
                      </Form.Item>
                      <div className="forms-row__actions">
                        {fields?.length > 2 && (
                          <CustomIcons
                            className="dynamic-delete-button"
                            type="garbage"
                            onClick={() => remove(name)}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}

                <Button className="add-sub-options" onClick={() => add()}>
                  Ajouter une option
                </Button>
              </>
            );
          }}
        </Form.List>
      </>
    );
  }, []);

  const generateFileldByType = useCallback(
    (element, index) => {
      const { key, name, fieldKey, ...restField } = element;
      let cur = dynamicForm.getFieldsValue();
      let curV =
        Object.keys(cur)?.length > 0 ? cur : { [formName]: currentValues };
      let filedsValues = curV[formName][`${formName}-fields`] || [];
      let rowValsType =
        filedsValues?.length > 0 && filedsValues[name]?.['field-type'];

      switch (rowValsType) {
        case 'input':
          return (
            <>
              <Form.Item
                {...restField}
                name={[name, 'field-input']}
                fieldKey={[fieldKey, 'field-input']}
                className="forms-field"
                rules={[
                  {
                    required: false,
                  },
                ]}
              >
                <Input disabled placeholder={`Courte réponse`} />
              </Form.Item>
            </>
          );
        case 'radio':
          return generateRadioOrCheckboxFiled(element, 'radio');
        case 'checkbox':
          return generateRadioOrCheckboxFiled(element, 'checkbox');
        case 'textarea':
          return (
            <>
              <Form.Item
                {...restField}
                name={[name, 'field-input']}
                fieldKey={[fieldKey, 'field-input']}
                className="forms-field"
                rules={[
                  {
                    required: false,
                  },
                ]}
              >
                <TextArea disabled row="4" placeholder={`Longue réponse`} />
              </Form.Item>
            </>
          );
        default:
          return <></>;
      }
    },
    [initialValues, currentValues],
  );

  /* Add new Filed Row */
  const fieldTypeList = [
    { label: 'Longue réponse', key: 'textarea' },
    { label: 'Choix multiple', key: 'checkbox' },
    { label: 'Choix unique', key: 'radio' },
    { label: 'Courte réponse', key: 'input' },
  ];
  const addNewTypeField = useCallback((type, addCalback) => {
    let defVals = {
      'field-title': '',
      'field-type': type,
      'field-input': null,
    };
    if (type == 'radio') {
      defVals['field-input'] = [
        { [`${type}-input`]: null },
        { [`${type}-input`]: null },
      ];
    } else if (type == 'checkbox') {
      defVals['field-input'] = [
        { [`${type}-input`]: null },
        { [`${type}-input`]: null },
      ];
    }
    !!setValidSection && setValidSection(name, false);
    setValidFields(false);
    addCalback(defVals);
  }, [setValidSection, name]);
  const dorpdownAddNewRow = useCallback(
    addCallback => (
      <div className="add-new-filed__wrapper">
        <Dropdown
          menu={{ items: fieldTypeList }}
          trigger={['hover']}
          className="add-new-filed"
          overlayClassName="add-new-filed__dropdown"
          getPopupContainer={trigger => trigger.parentElement}
          dropdownRender={menu => {
            const items = menu.props.items;
            return (
              <div className="dropdown-content">
                {items?.map(({ key, label }) => {
                  return (
                    <div
                      className="dropdown-content__item"
                      onClick={() => addNewTypeField(key, addCallback)}
                    >
                      {label}
                    </div>
                  );
                })}
              </div>
            );
          }}
          placement="top"
          arrow={{
            pointAtCenter: true,
          }}
        >
          <Button>
            <span className="add-new-filed__btn">Ajouter un champ</span>
          </Button>
        </Dropdown>
      </div>
    ),
    [addNewTypeField],
  );

  return (
    <div className={childClassNames}>
      <Form
        name={formName}
        form={dynamicForm}
        initialValues={{ [formName]: currentValues }}
        className="tcf-form-builder"
        autoComplete="off"
        onValuesChange={handleValuesChange}
      >
        <FBSectionHeader
          key="forms-section-header"
          type="forms"
          onDelete={() => deleteSection(formName)}
        />
        <div className="tcf-fb-group">
          <SectionFields key="forms" name={formName} sectionValues={currentValues} />
          <ErrorBoundary>
            <Form.List
              className="tcf-fb-group"
              name={[formName, `${formName}-fields`]}
              rules={[
                {
                  required: true,
                  validator: checkValidCallback,
                  validateTrigger: 'onChange',
                  message: 'Champ obligatoire',
                },
              ]}
            >
              {(fields, { add, remove, move }) => {
                return (
                  <>
                    {fields.map((element, index) => {
                      const { key, name, fieldKey, ...restField } = element;
                      return (
                        <div
                          key={
                            currentValues?.[`${formName}-fields`][name]?.['field-type'] + `${name}`
                          }
                          className="forms-group"
                          align="baseline"
                        >
                          <FBSectionFieldActions
                            key={`${formName}-section-action-${index}`}
                            type="forms"
                            index={name}
                            fieldType={
                              currentValues?.[`${formName}-fields`][name]?.['field-type']
                            }
                            fieldTypeList={fieldTypeList}
                            totalLength={
                              currentValues[`${formName}-fields`]?.length || 0
                            }
                            onMove={move}
                            onDelete={() => remove(name)}
                          />

                          <div className="forms-group__context">
                            <HiddenField
                              key={[name, `field-type`]}
                              data={{
                                name: [name, `field-type`],
                              }}
                              index={0}
                            />

                            <Form.Item
                              {...restField}
                              name={[name, 'field-title']}
                              fieldKey={[fieldKey, 'field-title']}
                              className="forms-title"
                              rules={[
                                {
                                  required: true,
                                  message: 'Champ obligatoire',
                                  validateTrigger: 'onChange',
                                },
                              ]}
                            >
                              <Input placeholder={`Titre du champ`} />
                            </Form.Item>

                            {generateFileldByType(element)}
                          </div>
                        </div>
                      );
                    })}

                    {dorpdownAddNewRow(add)}
                  </>
                );
              }}
            </Form.List>
          </ErrorBoundary>
          {erroMessage}
        </div>
      </Form>
    </div>
  );
}

FBForms.defaultProps = {
  name: 'forms',
  mode: 'new',
  method: 'nouvel',
};
FBForms.propTypes = {
  onDelete: PropTypes.func
};

export default memo(FBForms);
